import { useContext, useMemo, useReducer } from 'react'
import { MIDIInput, MIDIOutput } from './types/WebMidi'
import { MediaDeviceError } from './services/MediaDevices'
import { arrayInPlaceRemove, smartStringify } from './utils'
import { persistent } from './services/LocalStorage'
import { DesktopCapturerSource } from 'electron'
import { logger as rootLogger } from './services/Logger'
import clone from 'clone'
import React from 'react'
import { AudioEngineLocalStreamStats, AudioEngineError, AudioEngineState } from './core/AudioEngine'
import { RoomTierPolicy } from './RoomContext'
import { LocalVideoTrack } from 'livekit-client'
import { BackgroundBlur, VirtualBackground } from './tools/track-processors'
import { PerfStatsEmitter } from './pages/PerfStatsPage/PerfStatsEmitter'

const log = rootLogger.createChildLogger({ name: 'UserContext' })

export type DeviceId = string

export type ClientType = '' | 'listenonly'

export type ExtraVideoOptions = {
    optionFramerate?: number | null
    optionWidth?: number | null
    optionHeight?: number | null
    optionAspectRatio?: number | null
}

export type DesktopCapturerSourceExt = DesktopCapturerSource &
    ExtraVideoOptions & {
        screenIndex: number | null
        scaleFactor: number
        screenWidth: number | null
        screenHeight: number | null
        screenPositionX: number | null
        screenPositionY: number | null
        nonScaledBounds: { x: number; y: number; width: number; height: number } | null
    }

export type AudioSourceInfo = {
    // the below 4 from MediaDeviceInfo.
    deviceId: string
    groupId: string
    kind: MediaDeviceKind
    label: string

    nativeDeviceId: string | null
    audioEngineDeviceId: string | null
    deviceUid: string | null
    displayLabel: string
    defaultSampleRate: number
    maxInputChannels: number
    maxOutputChannels: number
    hostApiName: string
    isSystemAudio: boolean
    isMuseAudioShare: boolean
    isMuseDAWBridge: boolean

    // e.g. if "VB Audio Cable Input", then the matching loopback source is "VB Audio Cable Output".
    // This is set in `MediaDevices.getAudioAndVideoMediaSources().`
    matchingLoopbackSource?: AudioSourceInfo | null
}

export type UserGeoData = {
    city: string
    country: string
    countryCode: string
    lat: number
    lon: number
    regionName: string
    timezone: string
}

export type UserOrg = {
    id: string
    description: string
    isAdmin: boolean
}

export type UserProfilePolicy = {
    _id: string
    allowAudioEngineTx: boolean
    allowHighQualityAudioForNonSystemAudio: boolean
    allowScreenControl: boolean
    isRoomDestroyedOnLeave: boolean
    maxPerRoomFileUploadSizeMB: number
    maxTotalFileUploadSizeMB: number
    maxUploadFileSizeMB: number
    roomAutoDeleteDurationHr: number
    allowCustomSessionCode: boolean
    maxPermanentRoomCount: number
    canCreateRecurringSessions: boolean
    allowCreateListeningSessions: boolean
    canUseCloudRecording: boolean
    canUseAudioPatching: boolean
    roomTierPolicy: RoomTierPolicy
}

export type UserProfile = {
    username: string
    email: string
    emailVerified: boolean
    knownUsers: { userUrn: string; username: string }[]
    displayName?: string
    policy?: UserProfilePolicy
    geoData?: UserGeoData

    // boosts
    boostsRemaining: number
    boostsMax: number
    boostNextRechargeDate: Date | null

    // trial
    freeTrialEndDate: Date | null

    orgs: UserOrg[]
}

export function createUserProfileFromApiUserDocument(user: any): UserProfile {
    return {
        username: user?.username || '',
        email: user?.displayEmail || user?.email,
        displayName: user?.displayName || null,
        emailVerified: user?.emailVerified || false,
        policy: user?.policy || user?.tierPolicy /* legacy */ || {},
        knownUsers: user?.knownUsers || [],
        boostsRemaining: user?.boostsRemaining || 0,
        boostsMax: user?.boostsMax || 2,
        boostNextRechargeDate: user?.boostNextRechargeDate || null,
        freeTrialEndDate: user?.freeTrialEndDate ? new Date(user.freeTrialEndDate) : null,
        orgs: user.orgs,
    }
}

export type UserCommunityProfile = {
    name: string
    bio: string
    genre: string[]
    instrument: string[]
    role: string[]
}

export type UserCommunityProfileView = {
    userUrn: string
    username: string
    collaboratorStatus: string
    activeStatus: string
} & UserCommunityProfile

export type UserProfileWithUrn = {
    userUrn: string
} & UserProfile

export type UserProfileBasic = {
    username: string
    name: string | null
}

export type SessionUserProfile = {
    sessionId: string
    userUrn: string
    username: string
    name: string | null
}

export type SfuNode = {
    code: string
    name: string
    url: string
    lat: number
    lon: number
}

export type SubRoomFragment = { _id: string; name: string }

export type ParentRoomFragment = {
    _id: string
    name: string
    joinCode: string
    joinLink: string | null
    customJoinCode: string
    password: string | null
    isUserSubroomAssignmentActive: boolean
    isUserSubroomAutoAssigned: boolean
}

export type RoomInfo = {
    roomUrn: string | null
    joinCode: string | null
    joinLink: string | null
    customJoinCode: string | null
    customJoinLink: string | null
    password: string | null
    name: string
    host: UserProfileWithUrn | null
    sessionStartedTime?: Date
    permanent: boolean | null
    autoRecordSessions?: boolean
    allowUserAutoJoin: boolean
    suppressSessionFeedback: boolean
    joinLinkMetadata?: { [key: string]: string }
    parentRoomUrn: string | null
    parentRoom: ParentRoomFragment | null
    subrooms: SubRoomFragment[]
    org?: string
}

export type CommunityMessageGroup = {
    _id: string
    members: UserCommunityProfileView[]
    messages: any[]
    createdAt: string
    updatedAt: string
}

export type VideoInputOptions = {
    quality?: string
    width?: number
    height?: number
    frameRate?: number
}

export type MediaSelectionRecordingParams = {
    previewBeatsEnabled: boolean
    tempo: number
    timeSignature: [number, number]
    delta_dawcommand: number
    delta_alpha: number
    delta_beta: number
    saveLocalRecording: boolean
}

export type MediaPermissions = {
    desktop: boolean | null
    video: boolean | null
    audio: boolean | null
    midi: boolean | null
    accessibility: boolean | null
}

export type MediaPrefilghtStatusState = {
    isReady: boolean | null // tristate where 'null' is neutral.
    errorCode?: string // error code, if isReady is 'false'.
    message?: string
}

export type MediaPreflightStatus = {
    audioInput: MediaPrefilghtStatusState
    audioOutput: MediaPrefilghtStatusState
    audioDaw: MediaPrefilghtStatusState
    audioComputer: MediaPrefilghtStatusState
    midi: MediaPrefilghtStatusState
}

export type MediaSelection = {
    desktop: string
    desktopSourceInfo: DesktopCapturerSourceExt | null
    video: string
    previousVideo?: string
    talkbackMic: string | undefined
    audioInputs: AudioInputSelection[]
    audioOutput: string
    audioOutputMuseRecording: string
    midiInput: string | '_none' | '_undef'
    midiPassthroughOutput: string | '_none' | '_undef'
    midiOutput: string | '_none' | '_undef'

    globalOutputDevice: GlobalOutputDeviceType | null
    globalOutputDeviceHistory: GlobalOutputDeviceType[]
    lastGlobalOutputDeviceBeforeAutoSelection: GlobalOutputDeviceType | null
    undoGlobalOutputDeviceWhenScreenShareEnds: boolean
    isGlobalOutputDeviceChangeUpdating: boolean

    showSystemAudioOutputChangeConfirmation: boolean
    lastChangedMediaType: string
    lastSystemAudioShareFromScreenShare: boolean

    // Set to true once the default/prev-pref media selection is loaded.
    // Used to prevent jobs from messing with the mediaSelection before the default/prev-pref is
    // loaded -- since the action of updating the mediaSelection itself causes the prev-prev to
    // save.
    isDefaultMediaSelectionLoaded: boolean

    // devices removed without user intervention
    autoRemovedAudioInputs: { [key: string]: boolean }

    // Previous virtual background selected before muting the video device.
    selectedVirtualBackground: string | null
}

export type MediaSources = {
    // Used by listeners to know if the mediaSources had an incremental update, that is,
    // updated by a devicechange event in `navigator.mediaDevices`.
    // Some notifications/dialogs only make sense during incremental updates to the
    // mediaSources list.
    updatedByEventCt: number

    // incremented on every update to the mediaSources.
    updatedCt: number
    desktop: DesktopCapturerSourceExt[]
    video: MediaDeviceInfo[]
    virtualCamera: MediaDeviceInfo | null
    audioInputs: AudioSourceInfo[]
    audioOutputs: AudioSourceInfo[]
    audioOutputsAll: AudioSourceInfo[]
    midiInputs: MIDIInput[]
    midiOutputs: MIDIOutput[]
    loaded: boolean

    _triggerUpdateWithDebounce: number
}

export type AudioInputSettings = {
    sampleRate: number
    bitDepth: number
    enableLocalLoopback: boolean

    // Indicates if this audio input is using voice quality. This is independent
    // of the selected talkback mic.
    isVoiceQuality: boolean

    // valid when `isAudioEngine` is false; WebRTC high quality.
    // it is named this way for backwards compatibility reasons.
    isHighQuality: boolean

    // if true, then use Audio Engine if possible.
    isAudioEngine: boolean

    // list of channels to select, with grouping (mono vs stereo).
    // example: [[1,2],[4]] -- select ch. 1&2 as stereo and ch. 4 as mono.
    channelSelection: number[][]

    // NOTE: the below settings are valid when isAudioEngine is set.

    // valid if isAudioEngine is true. sets whether compression is enabled
    // on the audio engine.
    isCompressed: boolean
    compressionBitrate: number

    // the buffer size that will be given to the receiver.
    receiverBufferSizeMs: number
    // max drift from the receiver buffer size; 0 indicates not used.
    receiverBufferMaxDriftMs: number

    // advanced audio engine settings
    framesPerBuffer: number // reserved
    // how much audio a single packet should contain.
    // 0 for default (legacy) 20ms pcm / 40ms opus.
    networkPacketDurationMs: number
}

export type AudioTrackWithInfo = MediaStreamTrack & { info: AudioInputStreamInfo }

export type AudioInputSelection = {
    deviceId: string
    settings: AudioInputSettings
    source: AudioSourceInfo
}

export type AudioOutputSettings = {
    sampleRate: number
    bitDepth: number
    channelCount: number
    compressionBitrate: number
    isSelective: boolean
    includeStreams: string[]
    excludeStreams: string[]
    _isDeviceSampleRateOverridden?: boolean
}

export type AudioInputStreamInfo = {
    deviceId: string
    groupId: string
    nativeDeviceId: string | null
    audioEngineDeviceId: string | null
    audioEngineStreamUuid: string | null

    label: string
    displayLabel: string

    isSystemAudio: boolean
    isMuseAudioShare: boolean
    isMuseDAWBridge: boolean

    channelCount: number // computed from channelSelection
    channelSelection: number[][]
    channelPackingMap: number[]
    channelUnpackingMap: number[]
} & AudioInputSettings

export type MediaSettings = {
    audioInputs: { [key: DeviceId]: AudioInputSettings }
    audioOutputs: { [key: DeviceId]: AudioOutputSettings }
}

export type MediaSourceStatus = {
    isLoading: boolean
    audioInputs: {
        [key: string /*deviceId*/]: {
            isMuted: boolean
            // exists for audioengine devices
            localStreamStats: AudioEngineLocalStreamStats | null
        }
    }
    desktop: { isLoading: boolean }
}

export type AudioInputError = {
    message: string
    debugMessage: string
}

export type ContextMediaStreams = {
    desktop: MediaStream | MediaDeviceError | null
    desktopInfo: { settings: MediaTrackSettings; capabilities: MediaTrackCapabilities } | null
    video: LocalVideoTrack | MediaDeviceError | null
    videoLoading: boolean
    videoInfo: { settings: MediaTrackConstraints; capabilities: MediaTrackCapabilities; label: string } | null
    videoProcessor: {
        name: string
        processor: Awaited<ReturnType<typeof BackgroundBlur>> | Awaited<ReturnType<typeof VirtualBackground>>
        id: string
    } | null
    audioInputs: AudioTrackWithInfo[]
    audioInputErrors: AudioInputError[]
    audioEngineAudioOutputError: AudioEngineError | null
}

// {"opcode":"gl-odev-changed","nativeDeviceId":51,"label":"BlackHole 2ch","uid":"BlackHole2ch_UID"}
export type GlobalOutputDeviceType = {
    opcode: string
    nativeDeviceId: string
    label: string
    uid: string
}

export type MediaSelectionPrevPreference = {
    video: string | null
    audioInputs: string[] | null
    talkbackMic: string | null
    audioOutput: string | null
    audioOutputMuseRecording: string | null
    midiOutputDevice: string | null
    midiOutputPassthroughDevice: string | null
}

export type CommunityPageTabTypes = 'collaborate' | 'messages' | 'profile'

export type UserProfilesSearchResult = {
    page: number
    pageSize: number
    size: number
    profiles: UserCommunityProfileView[]
}

export type CommunityState = {
    userProfilesSearchResult: UserProfilesSearchResult
    collaborators: UserCommunityProfileView[]
    collaboratorInvitations: UserCommunityProfileView[]
    messageGroups: CommunityMessageGroup[]

    // A holding place for new unread messages, keyed by the message group id. The corresponding
    // undread messages are cleared when they are read.
    messageGroupUnreadMessagesPerGroup: Map<string, number>
    selectedMessageGroupId: string | null
    communityPageCurrentTab: CommunityPageTabTypes
}

export type ModelUpdateEventType = {
    modelName: string
    modelId?: string
    updatedFields: { [key: string]: any }
}

export type AppEvents = {
    uuid: string
    code: string
    timestamp: string // ISO date
    isRead: boolean
    [key: string]: any
}

export type SpecialLifetimeOfferNotificationData = {
    id: number
    __specialLifetimeOfferFlag: boolean
    __getLifetimeButtonLink: string
    __getLifetimeButtonText: string
    __learnMoreLink: string
    __learnMoreText: string
    __mdTop: string
    __mdMiddle: string
    __mdBottom: string
    __bottomAlertText: string
}

export type UserContextState = {
    userUrn: string
    isGuest: boolean
    clientType: ClientType
    authToken: string
    sessionId: string
    appSerialId: string

    profile: UserProfile | null
    communityProfile: UserCommunityProfile | null
    mediaPermissions: MediaPermissions
    mediaPreflightStatus: MediaPreflightStatus
    mediaSources: MediaSources
    mediaSourceStatus: MediaSourceStatus
    mediaSettings: MediaSettings
    mediaSelection: MediaSelection
    mediaStreams: ContextMediaStreams
    appEvents: AppEvents[]
    audioEngineState: AudioEngineState

    community: CommunityState

    reloadCounter: number
}

export type LoginObject = {
    authToken: string
    userUrn: string
    displayName?: string
    profile: UserProfile
    communityProfile: UserCommunityProfile | null
    isGuest: boolean
}

export type GuestLogin = {
    readonly type: 'guest_login'
    readonly value: { userUrn: string; authToken: string; clientType: ClientType }
}

export type Login = {
    readonly type: 'login'
    readonly value: LoginObject
}

export type Logout = {
    readonly type: 'logout'
    readonly value?: any
}

export type UpdateUserProfile = {
    readonly type: 'update_user_profile'
    readonly value: Partial<UserProfile>
}

export type SetUserCommunityProfile = {
    readonly type: 'set_user_community_profile'
    readonly value: UserCommunityProfile | null
}

export type UpdateUserCommunityProfile = {
    readonly type: 'update_user_community_profile'
    readonly value: Partial<UserCommunityProfile>
}

export type UpdateMediaSelection = {
    readonly type: 'update_media_selection'
    readonly value: Partial<MediaSelection>
}

export type UpdateMediaSelectionRemove = {
    readonly type: 'update_media_selection.remove'
    readonly value: {
        desktop?: string // the deviceId to remove.
        video?: string
        audioInput?: string
        audioOutput?: string
    }
}

export type ClearMediaSelectionAndStreams = {
    readonly type: 'clear_media_selection_and_streams'
    readonly value: any
}

export type ReloadRoot = {
    readonly type: 'reload_root'
    readonly value?: any
}

export type UpdateMediaSettings = {
    readonly type: 'update_media_settings'
    readonly value: Partial<MediaSettings>
}

export type UpdateMediaSources = {
    readonly type: 'update_media_sources'
    readonly value: Partial<MediaSources>
}

export type UpdateMediaSourceStatus = {
    readonly type: 'update_media_source_status'
    readonly value: Partial<MediaSourceStatus>
}

export type UpdateMediaStreams = {
    readonly type: 'update_media_streams'
    readonly value: Partial<ContextMediaStreams>
}

export type GlobalOutputDeviceChanged = {
    readonly type: 'global_output_device_changed'
    readonly value: GlobalOutputDeviceType
}

export type UpdateCommunityState = {
    readonly type: 'update_community_state'
    readonly value: Partial<CommunityState>
}

export type SetAppEvents = {
    readonly type: 'set_app_events'
    readonly value: AppEvents[]
}

export type AppendAppEvents = {
    readonly type: 'append_app_events'
    readonly value: AppEvents
}

export type SetAppIds = {
    readonly type: 'set_app_ids'
    readonly value: Partial<{ appSerialId: string; sessionId: string }>
}

export type UpdateMediaPermissions = {
    readonly type: 'update_media_permissions'
    readonly value: Partial<MediaPermissions>
}

export type UpdateMediaPreflightStatus = {
    readonly type: 'update_media_preflightstatus'
    readonly value: Partial<MediaPreflightStatus>
}

export type UpdateAudioEngineState = {
    readonly type: 'update_audio_engine_state'
    readonly value: Partial<AudioEngineState>
}

export type UserContextActions =
    | GuestLogin
    | Login
    | Logout
    | SetAppIds
    | UpdateUserProfile
    | SetUserCommunityProfile
    | UpdateUserCommunityProfile
    | UpdateMediaSelection
    | UpdateMediaSelectionRemove
    | ClearMediaSelectionAndStreams
    | UpdateMediaSettings
    | UpdateMediaSources
    | UpdateMediaSourceStatus
    | UpdateMediaStreams
    | ReloadRoot
    | GlobalOutputDeviceChanged
    | UpdateCommunityState
    | SetAppEvents
    | AppendAppEvents
    | UpdateMediaPermissions
    | UpdateMediaPreflightStatus
    | UpdateAudioEngineState

const initialUserContext: UserContextState = {
    userUrn: '',
    isGuest: false,
    clientType: '',
    authToken: '',
    sessionId: '',
    appSerialId: '',
    profile: null,
    communityProfile: null,
    mediaPermissions: {
        desktop: null,
        video: null,
        audio: null,
        midi: null,
        accessibility: null,
    },
    mediaPreflightStatus: {
        audioInput: { isReady: null, errorCode: '' },
        audioOutput: { isReady: null, errorCode: '' },
        audioDaw: { isReady: null, errorCode: '' },
        audioComputer: { isReady: null, errorCode: '' },
        midi: { isReady: null, errorCode: '' },
    },
    mediaSources: {
        updatedByEventCt: 0,
        updatedCt: 0,
        desktop: [],
        video: [],
        virtualCamera: null,
        audioInputs: [],
        audioOutputs: [],
        audioOutputsAll: [],
        midiInputs: [],
        midiOutputs: [],
        loaded: false,

        _triggerUpdateWithDebounce: 0,
    },
    mediaSettings: {
        audioInputs: {},
        audioOutputs: {},
    },
    mediaSourceStatus: {
        isLoading: false,
        // lookup by deviceId
        audioInputs: {},
        desktop: { isLoading: false },
    },
    mediaSelection: {
        desktop: '_undef',
        desktopSourceInfo: null,
        video: '_undef',
        talkbackMic: undefined,
        audioInputs: [],
        audioOutput: '_undef',
        audioOutputMuseRecording: '_undef',
        midiInput: '_undef',
        midiPassthroughOutput: '_undef',
        midiOutput: '_undef',
        globalOutputDevice: null,
        globalOutputDeviceHistory: [],
        lastGlobalOutputDeviceBeforeAutoSelection: null,
        undoGlobalOutputDeviceWhenScreenShareEnds: false,
        isGlobalOutputDeviceChangeUpdating: false,
        showSystemAudioOutputChangeConfirmation: false,
        lastChangedMediaType: 'desktop',
        lastSystemAudioShareFromScreenShare: false,
        isDefaultMediaSelectionLoaded: false,
        autoRemovedAudioInputs: {},
        selectedVirtualBackground: null,
    },
    mediaStreams: {
        desktop: null,
        desktopInfo: null,
        video: null,
        videoLoading: false,
        videoInfo: null,
        videoProcessor: null,
        audioInputs: [],
        audioInputErrors: [],
        audioEngineAudioOutputError: null,
    },
    community: {
        userProfilesSearchResult: { page: 0, size: 0, pageSize: 0, profiles: [] },
        collaborators: [],
        collaboratorInvitations: [],
        messageGroups: [],
        messageGroupUnreadMessagesPerGroup: new Map(),
        selectedMessageGroupId: null,
        communityPageCurrentTab: 'collaborate',
    },
    audioEngineState: {
        isEnabled: true, // true for now, link this with permissions later
        isLoaded: false,
        errorMessage: null,
        lastConnectionEvent: 'CONNECTING',
    },
    appEvents: [],
    reloadCounter: 0,
}

export const loadInitialUserContext = () => {
    try {
        const userCache: LoginObject = (persistent.getObject('userCache') || {}) as LoginObject
        console.log('loadInitialUserContext: %O', userCache)
        if (userCache?.isGuest) {
            // is guest user who refreshed the brower; log out.
            throw new Error(`Guest user`)
        }

        if (typeof userCache.authToken !== 'string' || userCache.authToken.trim().length === 0) {
            throw new Error(`Invalid authToken`)
        }

        if (typeof userCache.userUrn !== 'string' || userCache.userUrn.trim().length === 0) {
            throw new Error(`Invalid userUrn`)
        }
        initialUserContext.userUrn = userCache.userUrn

        if (typeof userCache.profile !== 'object') {
            throw new Error(`Invalid profile`)
        }
        initialUserContext.profile = userCache.profile
        initialUserContext.authToken = userCache.authToken
        initialUserContext.isGuest = userCache.isGuest
    } catch (e) {
        localStorage.removeItem('userCache')
    }

    return initialUserContext
}

let reduceCt = 0
function userContextReducer(state: UserContextState, action: UserContextActions): UserContextState {
    log.isVerbose() && log.verbose(`updateUserContext`, `type="${action.type}"`)
    let stateNext = state

    switch (action.type) {
        case 'guest_login':
            // NOTE: Dispatching a guest_login, will cause App to loadUser()
            // which will then dispatch a real 'login'.
            persistent.set('userCache', action.value)
            stateNext = {
                ...state,
                userUrn: action.value.userUrn,
                isGuest: true,
                clientType: action.value.clientType,
                authToken: action.value.authToken,
            }
            break
        case 'login':
            const userCache: LoginObject = action.value
            persistent.set('userCache', userCache)

            stateNext = {
                ...state,
                userUrn: action.value.userUrn,
                isGuest: action.value.isGuest ?? false,
                authToken: action.value.authToken,
                profile: action.value.profile,
                communityProfile: action.value.communityProfile,
            }
            break
        case 'logout': {
            localStorage.removeItem('userCache')

            stateNext = {
                ...state,
                userUrn: '',
                profile: null,
            }
            break
        }
        case 'set_app_ids': {
            stateNext = {
                ...state,
                ...action.value,
            }
            break
        }
        case 'update_user_profile':
            if (state.profile) {
                stateNext = {
                    ...state,
                    profile: {
                        ...state.profile,
                        ...action.value,
                    },
                }
            } else {
                stateNext = state
            }
            break
        case 'set_user_community_profile':
            stateNext = {
                ...state,
                communityProfile: action.value,
            }
            break
        case 'update_user_community_profile':
            if (state.communityProfile) {
                stateNext = {
                    ...state,
                    communityProfile: {
                        ...state.communityProfile,
                        ...action.value,
                    },
                }
            } else {
                stateNext = state
            }
            break
        case 'update_media_selection':
            const value: Partial<MediaSelection> = { ...action.value }
            if (value.desktop) {
                value.desktopSourceInfo =
                    state.mediaSources.desktop.filter((d) => d.id === action.value.desktop)[0] || null
            }

            stateNext = {
                ...state,
                mediaSelection: {
                    ...state.mediaSelection,
                    ...value,
                },
            }
            break
        case 'update_media_selection.remove':
            let mediaSelection_next = clone(state.mediaSelection)
            // NOTE: the action.value.* is the deviceId to remove.
            if (action.value.audioInput) {
                arrayInPlaceRemove(mediaSelection_next.audioInputs, (a) => a.deviceId === action.value.audioInput)
            }
            if (action.value.audioOutput) {
                mediaSelection_next.audioOutput = '_none'
            }
            if (action.value.desktop) {
                mediaSelection_next.desktop = '_none'
            }
            if (action.value.video) {
                mediaSelection_next.video = '_none'
            }
            stateNext = {
                ...state,
                mediaSelection: mediaSelection_next,
            }
            break
        case 'clear_media_selection_and_streams':
            // kill all the streams
            state.mediaStreams.audioInputs.forEach((a) => {
                try {
                    a.stop()
                } catch (e) {}
            })
            if (state.mediaStreams.desktop instanceof MediaStream) {
                const desktopStream = state.mediaStreams.desktop
                desktopStream.getTracks().forEach((t) => {
                    try {
                        t.stop()
                    } catch (e) {}
                })
            }
            if (state.mediaStreams.video instanceof LocalVideoTrack) {
                const videoStream = state.mediaStreams.video
                videoStream.stop()
            }

            stateNext = {
                ...state,
                mediaSelection: {
                    ...initialUserContext.mediaSelection,
                    // don't reset these fields
                    globalOutputDevice: state.mediaSelection.globalOutputDevice,
                    globalOutputDeviceHistory: state.mediaSelection.globalOutputDeviceHistory,
                    lastGlobalOutputDeviceBeforeAutoSelection:
                        state.mediaSelection.lastGlobalOutputDeviceBeforeAutoSelection,
                    autoRemovedAudioInputs: state.mediaSelection.autoRemovedAudioInputs,
                },
                mediaStreams: {
                    ...initialUserContext.mediaStreams,
                },
                mediaSources: {
                    ...state.mediaSources,
                    loaded: false,
                },
            }

            break
        case 'update_media_settings':
            stateNext = {
                ...state,
                mediaSettings: {
                    ...state.mediaSettings,
                    ...action.value,
                },
            }
            break
        case 'update_media_sources':
            let eventData = { ...action.value }
            if (eventData.desktop) {
                eventData.desktop = eventData.desktop.filter((a) => (a?.id || '').startsWith('screen:'))
            }
            rootLogger.event({ page: 'usercontext', action: 'update_media_sources', data: { ...eventData } })

            stateNext = {
                ...state,
                mediaSources: {
                    ...state.mediaSources,
                    ...action.value,
                    updatedCt: state.mediaSources.updatedCt + 1,
                },
            }
            break
        case 'update_media_source_status':
            stateNext = {
                ...state,
                mediaSourceStatus: {
                    ...state.mediaSourceStatus,
                    ...action.value,
                },
            }
            break
        case 'update_media_streams': {
            const updated = { ...action.value }
            if (updated.desktop instanceof MediaStream) {
                const videoTrack = updated.desktop.getVideoTracks()[0]
                updated.desktopInfo = {
                    settings: videoTrack.getSettings(),
                    capabilities: videoTrack.getCapabilities(),
                }
            }
            if (updated.video instanceof LocalVideoTrack) {
                const videoTrack = updated.video.mediaStreamTrack
                updated.videoInfo = {
                    settings: videoTrack.getSettings(),
                    capabilities: videoTrack.getCapabilities(),
                    label: videoTrack.label,
                }
            }

            stateNext = {
                ...state,
                mediaStreams: {
                    ...state.mediaStreams,
                    ...updated,
                },
            }
            break
        }
        case 'global_output_device_changed': {
            const next = { ...state }
            next.mediaSelection.globalOutputDevice = action.value
            next.mediaSelection.globalOutputDeviceHistory.push(action.value)
            stateNext = next

            // @ts-ignore
            window.globalOutputDeviceNativeDeviceId = action.value.nativeDeviceId

            break
        }
        case 'update_community_state': {
            const next = { ...state.community, ...action.value }
            stateNext = {
                ...state,
                community: next,
            }
            break
        }
        case 'set_app_events': {
            stateNext = {
                ...state,
                appEvents: action.value,
            }
            break
        }
        case 'append_app_events': {
            stateNext = {
                ...state,
                appEvents: [action.value, ...state.appEvents],
            }
            break
        }
        case 'update_media_permissions': {
            stateNext = {
                ...state,
                mediaPermissions: {
                    ...state.mediaPermissions,
                    ...action.value,
                },
            }
            break
        }
        case 'update_media_preflightstatus': {
            stateNext = {
                ...state,
                mediaPreflightStatus: {
                    ...state.mediaPreflightStatus,
                    ...action.value,
                },
            }
            break
        }
        case 'update_audio_engine_state': {
            const audioEngineState = {
                ...state.audioEngineState,
                ...action.value,
            }

            stateNext = {
                ...state,
                audioEngineState,
            }
            break
        }
        case 'reload_root':
            stateNext = { ...state, reloadCounter: state.reloadCounter + 1 }
            break

        default:
            neverReached(action)
            return state
    }

    log.isSilly() &&
        log.silly(`userContextReducer`, `updateResult`, {
            timestamp: new Date(),
            ct: reduceCt++,
            actionType: action.type,
            actionValue: smartStringify(action.value),
            stateDiff: {},
            stateNext: smartStringify(stateNext),
        })

    PerfStatsEmitter.inc('userContextDispatchCt')

    return stateNext
}
function neverReached(never: never) {}

export type UserContextType = {
    state: UserContextState
    dispatch: React.Dispatch<UserContextActions>
}
export const UserContext = React.createContext<UserContextType | undefined>(undefined)

export const UserContextProvider = ({ children }: any) => {
    const initialState = useMemo(() => {
        return loadInitialUserContext()
    }, [])

    const [state, dispatch] = useReducer(userContextReducer, initialState)
    return <UserContext.Provider value={{ state, dispatch }}>{children}</UserContext.Provider>
}

export const useUserContext = (): [UserContextState, React.Dispatch<UserContextActions>] => {
    const context = useContext(UserContext)

    if (context === null) {
        throw new Error('useUserContext must be used within a UserContextProvider')
    }
    return [context?.state!, context?.dispatch!]
}
